import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import { toast } from "sonner";
import { Eye, RefreshCw } from "lucide-react";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });
const STATUS = {
  balanced: { l: "Denk",  cls: "text-emerald-700" },
  over:     { l: "Fazla", cls: "text-blue-700" },
  short:    { l: "Eksik", cls: "text-red-600" },
};

export default function CashCloseList() {
  const { isSuperAdmin } = useUsmAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ date_from: "", date_to: "", branch_id: "" });
  const [branches, setBranches] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter.date_from) qs.set("date_from", filter.date_from);
      if (filter.date_to) qs.set("date_to", filter.date_to);
      if (filter.branch_id) qs.set("branch_id", filter.branch_id);
      qs.set("limit", "100");
      const r = await usmApi.get(`/cash-close?${qs.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error("Geçmiş yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isSuperAdmin) usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, [isSuperAdmin]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.date_from, filter.date_to, filter.branch_id]);

  return (
    <div data-testid="usm-cash-history">
      <PageHeader title="Kasa Kapanış Geçmişi" breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Kasa Kapanış", to: "/usm/cash-close" }, { label: "Geçmiş" }]} />

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <input type="date" value={filter.date_from} onChange={(e) => setFilter((f) => ({ ...f, date_from: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        <input type="date" value={filter.date_to} onChange={(e) => setFilter((f) => ({ ...f, date_to: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        {isSuperAdmin && (
          <select value={filter.branch_id} onChange={(e) => setFilter((f) => ({ ...f, branch_id: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <button onClick={load} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]"><RefreshCw size={14} /></button>
        <div className="ml-auto text-xs text-[#71717A] inline-flex items-center">{total} kayıt</div>
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-3 py-3">Kapanış No</th>
              <th className="text-left px-3 py-3">Gün</th>
              <th className="text-left px-3 py-3">Şube</th>
              <th className="text-left px-3 py-3">Çalışan</th>
              <th className="text-right px-3 py-3">Beklenen</th>
              <th className="text-right px-3 py-3">Sayılan</th>
              <th className="text-right px-3 py-3">Fark</th>
              <th className="text-left px-3 py-3">Durum</th>
              <th className="text-right px-3 py-3 w-16">Detay</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
              : items.length === 0 ? <tr><td colSpan={9} className="px-4 py-10 text-center text-[#71717A]">Hiç kayıt yok.</td></tr>
              : items.map((c) => {
                const st = STATUS[c.status] || { l: c.status, cls: "" };
                return (
                  <tr key={c.id} data-testid={`cash-row-${c.cash_close_no}`} className="border-t border-[#F5F5F4]">
                    <td className="px-3 py-3 font-mono text-[12px] font-semibold">{c.cash_close_no}</td>
                    <td className="px-3 py-3">{c.business_day}</td>
                    <td className="px-3 py-3">{c.branch?.name || "—"}</td>
                    <td className="px-3 py-3 text-[#52525B]">{c.employee?.full_name || c.employee?.username || "—"}</td>
                    <td className="px-3 py-3 text-right font-finance">{TRY.format(c.expected_cash || 0)}</td>
                    <td className="px-3 py-3 text-right font-finance">{TRY.format(c.actual_cash || 0)}</td>
                    <td className={"px-3 py-3 text-right font-finance " + st.cls}>{c.difference > 0 ? "+" : ""}{TRY.format(c.difference || 0)}</td>
                    <td className={"px-3 py-3 text-[11px] uppercase tracking-[0.15em] " + st.cls}>● {st.l}</td>
                    <td className="px-3 py-3 text-right">
                      <Link to={`/usm/cash-close/${c.id}`} className="p-2 inline-flex hover:bg-[#FAFAF9]"><Eye size={14} /></Link>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
